'use client';

import React, {
  useRef,
  useState,
  useMemo,
  useCallback,
  useEffect,
} from 'react';
import { useSearchBox } from 'react-instantsearch';
import { debounce } from 'lodash';
import classNames from 'classnames';

import IconSearch from 'components/icons/IconSearch';
import useScreen from 'hooks/useScreen';
import useAdvancedSearchURL from '../../hooks/useAdvancedSearchURL';

const AlgoliaSearchBox = ({
  inMobileNav = false,
  isActive,
  placeholder = 'What can we help you find?',
  variant,
  onQueryChanging,
}) => {
  const { isMobile } = useScreen();
  const { query, refine, clear } = useSearchBox();
  const {
    queryUrlParam,
    updateUrlParam,
    updateUrlParamImmediate,
  } = useAdvancedSearchURL();
  const inputRef = useRef(null);
  const inputValueRef = useRef('');
  const isInitializingRef = useRef(true);

  const [inputValue, setInputValue] = useState(() => query || '');

  inputValueRef.current = inputValue;

  useEffect(() => {
    if (query && query !== inputValue) {
      setInputValue(query);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!inputRef.current || document.activeElement !== inputRef.current) {
      if (query !== inputValue) {
        setInputValue(query || '');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  useEffect(() => {
    const focused =
      inputRef.current && document.activeElement === inputRef.current;

    // On initial mount, just sync the UI with URL param without calling refine()
    // (InstantSearch has already initialized with the URL param via initialUiState)
    if (isInitializingRef.current) {
      if (queryUrlParam && !focused) {
        setInputValue(queryUrlParam);
      }
      isInitializingRef.current = false;
      return;
    }

    // On subsequent updates, refine the query if URL param changes and doesn't match current state
    if (
      !query &&
      queryUrlParam &&
      !focused &&
      queryUrlParam !== inputValueRef.current
    ) {
      refine(queryUrlParam);
      setInputValue(queryUrlParam);
    }
  }, [query, queryUrlParam, refine]);

  const debouncedSearch = useMemo(
    () =>
      debounce(value => {
        if (onQueryChanging) {
          onQueryChanging(value || null);
        }
        refine(value);
        updateUrlParam({ q: value || undefined });
      }, 500),
    [refine, updateUrlParam, onQueryChanging]
  );

  const handleInputChange = useCallback(
    event => {
      const newValue = event.target.value;
      setInputValue(newValue);
      debouncedSearch(newValue);
    },
    [debouncedSearch]
  );

  const handleReset = useCallback(() => {
    setInputValue('');
    debouncedSearch.cancel();
    if (onQueryChanging) {
      onQueryChanging(null);
    }
    clear();
    updateUrlParamImmediate({ q: undefined });
    inputRef.current?.focus();
  }, [clear, debouncedSearch, updateUrlParamImmediate, onQueryChanging]);

  const hasValue = inputValue && inputValue.length > 0;

  return (
    <div
      className={classNames(
        '[&_i]:w-[18px] [&_i]:h-[18px] [&_i]:text-[18px] [&_i]:block [&_i]:absolute [&_i]:top-1/2 [&_i]:mt-[-9px] [&_i]:right-10 [&_i]:cursor-pointer [&_i]:[border-right:1px_solid_#dfe0e1] bg-gray-light8',
        {
          'py-3': variant === 'ConsumerInstantSearch',
        }
      )}
    >
      <div
        className={classNames(
          'mx-5 my-0 flex flex-row items-stretch relative box-border min-[1440px]:mx-[3.333vw] min-[1440px]:my-0',
          {
            'px-0 py-[5px]': variant !== 'ConsumerInstantSearch',
            '!ml-3': inMobileNav,
            'px-3 bg-white rounded-[5px] border border-gray-light6 [&_.ais-SearchBox-input]:lg:!pl-0 [&_.ais-SearchBox-input]:lg:!text-left [&_.ais-SearchBox-input]:lg:!indent-[12px] [&_.ais-SearchBox-input::placeholder]:lg:!text-left [&_.ais-SearchBox-input.hasValue]:lg:!indent-[24px]':
              variant === 'ConsumerInstantSearch',
            '[&_button.ais-SearchBox-reset]:!block':
              variant === 'ConsumerInstantSearch' && isActive,
            '[&_.ais-SearchBox-input]:indent-[12px]':
              variant === 'InstantSearchMobile',
          }
        )}
      >
        <IconSearch
          className={classNames('self-center min-w-[16px]', {
            hidden: variant === 'InstantSearchMobile',
          })}
          width={18}
          height={18}
        />
        <div className="inline-flex w-full relative">
          <input
            ref={inputRef}
            type="search"
            value={inputValue}
            onChange={handleInputChange}
            placeholder={placeholder}
            className={classNames(
              'w-full border-none outline-none bg-transparent text-base py-2 pl-[30px]',
              'pl-[30px] indent-[unset] text-left',
              'text-[#333] [font-size:1rem] font-bold h-10 leading-[1.45rem] tracking-[-.0125rem]',
              {
                'lg:!pl-0 lg:!text-left lg:!indent-[12px]':
                  variant === 'ConsumerInstantSearch',
                'indent-[12px]': variant === 'InstantSearchMobile',
                'lg:!indent-[24px]':
                  variant === 'ConsumerInstantSearch' && hasValue,
              }
            )}
          />
          {hasValue && variant === 'ConsumerInstantSearch' && isActive && (
            <button
              type="button"
              onClick={handleReset}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center cursor-pointer bg-transparent border-none text-gray hover:text-gray-dark"
              aria-label="Clear search"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                width="16"
                height="16"
              >
                <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
              </svg>
            </button>
          )}
        </div>
      </div>
      {isMobile && (
        <div
          className={classNames(
            'h-0 w-0 top-[65px] z-[-1] left-0 fixed bg-white [transition:opacity_.3s_ease-out] opacity-0 lg:absolute lg:bg-transparent',
            {
              active: isActive,
              hidden: variant === 'InstantSearchMobile',
              'lg:opacity-100 lg:h-screen lg:w-screen lg:top-[-100px] lg:absolute lg:[transition:opacity_.3s_ease-out]': isActive,
            }
          )}
        />
      )}
    </div>
  );
};

export default AlgoliaSearchBox;
